import React, { FC, useState } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import {
  Box,
  ButtonAppearance,
  Icon,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSubHeader,
  Spinner,
  Text,
  useModalState,
  theme,
} from '@bechtel/gds';
import {
  AssignWeldForemanError,
  AssignWeldForemanInput,
  AssignWeldForemanPayload,
  CommodityMilestone,
  CommodityStatusCode,
  CrewMember,
} from '@bechtel/types';
import { ASSIGN_WELDING_FOREMAN } from '@bechtel/client-gql';
import { AssignWeldError } from '../../weld/error';
import { Field, FieldArray, Form, Formik, FormikProps } from 'formik';
import { findIndex, get } from 'lodash';
import { TitleValue, TextWithEllipsis } from '@bechtel/client-common';
import { useCommodity } from '~/detail/commodities/context';
import { useWorkPackage } from '~/detail/context';
import { AssignFooterButtonStyled, WeldAssignedStyled } from './styled';
import { WeldUserItem } from './weld-user-item';
import { t, Trans } from '@lingui/macro';
import { WeldStatusMessages } from '~/components';

interface FormikState {
  assignedList: {
    commodityId: string;
    typeId: string;
    foreman: CrewMember;
  }[];
}

const initialFormikValues: FormikState = {
  assignedList: [],
};

interface WeldMilestoneAssignProps {
  onClose: () => void;
  milestone: CommodityMilestone;
}

export const WeldoutMilestoneAssign: FC<WeldMilestoneAssignProps> = ({ onClose, milestone }) => {
  const wp = useWorkPackage();
  const { typeName: commodityType, tag: commodityTag } = useCommodity();
  const { close: closeModal, open: openErrorModal } = useModalState(false);
  const [assignWeldForemanError, setAssignWeldForemanError] = useState<AssignWeldForemanError | ApolloError | null>();
  const welds = milestone.weldStatuses;

  const [assignWeldForeman, { loading: mutationLoading }] = useMutation<
    { assignWeldForeman: AssignWeldForemanPayload },
    { input: AssignWeldForemanInput }
  >(ASSIGN_WELDING_FOREMAN, {
    onCompleted: (data) => {
      const assignError = get(data, 'assignWeldForeman.error');
      if (assignError) {
        setAssignWeldForemanError(assignError);
        openErrorModal();
      } else {
        onClose();
      }
    },
    onError: (error) => {
      setAssignWeldForemanError(error);
      openErrorModal();
    },
  });

  const closeErrorModal = () => {
    setAssignWeldForemanError(null);
    closeModal();
  };

  return (
    <Formik
      initialValues={initialFormikValues}
      onSubmit={async (values: FormikState) => {
        const { assignedList } = values;

        await assignWeldForeman({
          variables: {
            input: {
              workPackageId: wp.id,
              weldForemanInput: assignedList.map((item) => {
                return {
                  foremanId: item.foreman.id,
                  commodityId: item.commodityId,
                  typeId: item.typeId,
                };
              }),
            },
          },
        });
      }}
    >
      {({ values, submitForm, dirty, isValid }: FormikProps<FormikState>) => (
        <Form>
          <SideSheet visible onDismiss={onClose} style={{ width: `${theme.breakpoints.mobile}` }}>
            <SideSheetHeader title={t`Assign/Unassign Foreman`}>
              <SideSheetClose onClick={onClose} />
            </SideSheetHeader>
            <SideSheetSubHeader>
              <TitleValue title={t`Work Package`} value={wp.tag} data-testid="wpTag"></TitleValue>
              <TitleValue title={commodityType} value={commodityTag} data-testid="commodityType"></TitleValue>
            </SideSheetSubHeader>

            <SideSheetBody>
              <SideSheetContentCol>
                <FieldArray name="assignedList">
                  {(arrayHelpers) => {
                    return (
                      <>
                        {welds &&
                          welds.map((item) => {
                            const { id, tag, type, status } = item;
                            return (
                              <WeldAssignedStyled key={id} data-testid={`assignee-${id}`}>
                                <Box
                                  pb={{ sm: '500' }}
                                  pt={{ sm: '400' }}
                                  justifyContent={{ sm: 'space-between' }}
                                  alignItems={{ sm: 'center' }}
                                  css={`
                                    min-width: 0; /* required to work flex-basis, if removed flex-basis will not truncate text with ellipses.*/
                                  `}
                                >
                                  <Box alignItems={{ sm: 'center' }} mr={{ sm: '300' }}>
                                    <Text size="400" weight="600" color="grey.1000" family="graphik">
                                      <Trans> Weld:</Trans>
                                    </Text>
                                    <Box ml={{ sm: '200' }}>
                                      <TextWithEllipsis
                                        size="400"
                                        color="grey.1000"
                                        family="graphik"
                                        data-testid="weldInfo"
                                      >
                                        {tag}
                                      </TextWithEllipsis>
                                    </Box>
                                  </Box>
                                  {status?.status ===
                                    (CommodityStatusCode.VT_APPROVED || CommodityStatusCode.CLAIMED) && (
                                    <Box
                                      alignItems={{ sm: 'center' }}
                                      css={`
                                        min-width: 0; /* required to work flex-basis, if removed flex-basis will not truncate text with ellipses.*/
                                      `}
                                    >
                                      <Box mr={{ sm: '300' }}>
                                        <Icon icon="check" />
                                      </Box>
                                      {status?.status && (
                                        <TextWithEllipsis size="300" color="grey.1000">
                                          <Trans id={WeldStatusMessages[status.status].id} />
                                        </TextWithEllipsis>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                                <Box>
                                  <Field name={`assignedList.${id}`}>
                                    {({ field }) => {
                                      return (
                                        <WeldUserItem
                                          {...field}
                                          weld={item}
                                          onSelect={(item) => {
                                            const assigned = {
                                              foreman: item,
                                              commodityId: id,
                                              typeId: type.id,
                                            };
                                            const index = findIndex(values.assignedList, {
                                              commodityId: id,
                                            });
                                            if (index !== -1) {
                                              arrayHelpers.replace(index, assigned);
                                            } else {
                                              arrayHelpers.push(assigned);
                                            }
                                          }}
                                        ></WeldUserItem>
                                      );
                                    }}
                                  </Field>
                                </Box>
                              </WeldAssignedStyled>
                            );
                          })}
                      </>
                    );
                  }}
                </FieldArray>
              </SideSheetContentCol>
            </SideSheetBody>
            <SideSheetFooter>
              <Box justifyContent={{ sm: 'flex-end' }}>
                <Box mr={{ sm: '400' }}>
                  <AssignFooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onClose}>
                    <Trans> Cancel</Trans>
                  </AssignFooterButtonStyled>
                </Box>
                <Box>
                  <AssignFooterButtonStyled
                    data-testid="confirm"
                    onClick={submitForm}
                    appearance={ButtonAppearance.SECONDARY}
                    disabled={mutationLoading || !dirty || !isValid || !values?.assignedList?.length}
                  >
                    {mutationLoading ? <Spinner data-testid="loading" /> : <Trans>Confirm</Trans>}
                  </AssignFooterButtonStyled>
                </Box>
              </Box>
            </SideSheetFooter>
          </SideSheet>
          {assignWeldForemanError && (
            <AssignWeldError
              data-testid="error"
              assignWeldForemanError={assignWeldForemanError}
              close={closeErrorModal}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};
